import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ExternalLink, Search, RefreshCw, Radio, MapPin, User, Navigation } from "lucide-react";
import { TspCard, TspTable } from "../../../components/primitives";
import TspApiService from "../../../api/tspApiService";
import { initialsOf, formatRelativeTime } from "../../../utils/tspFormat";

const POLL_INTERVAL_MS = 30000; // Auto-refresh live locations every 30s

export default function LiveUsersLocationCard() {
  const [data, setData] = useState({ online_count: 0, total_count: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'online' | 'offline'

  const loadData = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      if (typeof TspApiService?.userLocations?.listLive === "function") {
        const res = await TspApiService.userLocations.listLive();
        if (res && res.data) {
          const users = (Array.isArray(res.data.users) ? res.data.users : []).map((u) => ({
            ...u,
            user_id: u.user_id || u.id,
          }));
          const total_count = res.data.total_count != null ? res.data.total_count : users.length;
          const online_count = res.data.online_count != null ? res.data.online_count : users.filter((u) => u.is_online).length;
          setData({
            users,
            total_count,
            online_count,
            timestamp: res.data.timestamp || null,
          });
        }
      }
    } catch (err) {
      console.warn("Failed to load live users location:", err?.message || err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData(true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadData]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.users || []).filter((user) => {
      if (filterMode === "online" && !user.is_online) return false;
      if (filterMode === "offline" && user.is_online) return false;
      if (!q) return true;
      return (
        user.name?.toLowerCase().includes(q) ||
        user.phone?.toLowerCase().includes(q) ||
        user.mobile_number?.toLowerCase().includes(q) ||
        user.temple?.toLowerCase().includes(q) ||
        user.temple_name?.toLowerCase().includes(q) ||
        user.role?.toLowerCase().includes(q)
      );
    });
  }, [data.users, search, filterMode]);

  const columns = [
    {
      key: "user",
      label: "User",
      render: (row) => (
        <div className="tsp-user-cell">
          <span className="tsp-user-avatar" aria-hidden="true">
            {initialsOf(row.name)}
          </span>
          <div className="tsp-user-details">
            <strong className="tsp-cell-strong">{row.name}</strong>
            {(row.phone || row.mobile_number) && (
              <span className="tsp-cell-muted">{row.phone || row.mobile_number}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "temple",
      label: "Temple / Role",
      render: (row) => (
        <div className="tsp-temple-cell">
          <span>{row.temple || row.temple_name || "—"}</span>
          <span className="tsp-role-badge">{row.role || "Member"}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={`tsp-live-status-pill ${row.is_online ? "is-online" : "is-offline"}`}>
          <span className="tsp-live-status-dot" />
          {row.is_online ? "Live Now" : "Offline"}
        </span>
      ),
    },
    {
      key: "location",
      label: "GPS Location",
      render: (row) => (
        <div className="tsp-coords-cell">
          <span className="tsp-coords-val">
            {row.latitude.toFixed(4)}, {row.longitude.toFixed(4)}
          </span>
          {row.accuracy && <span className="tsp-accuracy-hint">(&plusmn;{row.accuracy}m)</span>}
        </div>
      ),
    },
    {
      key: "last_seen",
      label: "Last Ping",
      render: (row) => (
        <span className="tsp-last-ping">
          {row.is_online ? "Just now" : row.last_seen_at ? formatRelativeTime(row.last_seen_at) : "—"}
        </span>
      ),
    },
    {
      key: "action",
      label: "Google Maps",
      render: (row) => (
        <a
          href={row.google_maps_url}
          target="_blank"
          rel="noopener noreferrer"
          className="tsp-btn tsp-btn-outline tsp-btn-sm tsp-google-maps-btn"
          title={`Open ${row.name}'s location in Google Maps`}
        >
          <MapPin size={13} className="tsp-map-pin-icon" /> Google Maps <ExternalLink size={12} />
        </a>
      ),
    },
  ];

  return (
    <TspCard
      className="tsp-live-users-card"
      title={
        <span className="tsp-card-title-with-badge">
          <Radio size={18} className="tsp-live-radio-icon" />
          Live Users GPS Location
          <span className="tsp-live-badge-count">
            <span className="tsp-pulse-dot" />
            {data.online_count} Online
          </span>
        </span>
      }
      action={
        <div className="tsp-card-action-group">
          <button
            type="button"
            className="tsp-btn tsp-btn-outline tsp-btn-sm"
            onClick={() => loadData(false)}
            disabled={refreshing}
            title="Refresh locations"
          >
            <RefreshCw size={13} className={refreshing ? "tsp-spin" : ""} /> Refresh
          </button>
        </div>
      }
    >
      <div className="tsp-live-users-toolbar">
        <label className="tsp-live-users-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by user, phone or temple..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <div className="tsp-live-filter-tabs">
          <button
            type="button"
            className={`tsp-filter-tab ${filterMode === "all" ? "active" : ""}`}
            onClick={() => setFilterMode("all")}
          >
            All ({data.total_count})
          </button>
          <button
            type="button"
            className={`tsp-filter-tab ${filterMode === "online" ? "active" : ""}`}
            onClick={() => setFilterMode("online")}
          >
            🟢 Online ({data.online_count})
          </button>
          <button
            type="button"
            className={`tsp-filter-tab ${filterMode === "offline" ? "active" : ""}`}
            onClick={() => setFilterMode("offline")}
          >
            ⚪ Offline ({Math.max(0, data.total_count - data.online_count)})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="tsp-live-users-loading">
          <RefreshCw size={18} className="tsp-spin" /> Loading live user positions...
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="tsp-live-users-empty">
          <Navigation size={28} />
          <p>
            {search
              ? "No live users matching your search."
              : "No user live locations recorded yet. Users will appear here automatically when they log into the TSP portal with GPS enabled."}
          </p>
        </div>
      ) : (
        <TspTable columns={columns} rows={filteredUsers} rowKey="user_id" className="tsp-table-live-users" />
      )}
    </TspCard>
  );
}
